import React, { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Ad placement wrapper. Renders ONLY for Free-tier users and only after a
 * study session ends (never mid-study). Paid users (Plus/Edu/Team/admins)
 * see nothing. After deploy we'll flip ADS_ENABLED to true and paste the
 * AdSense slot IDs — zero code changes required elsewhere.
 *
 * Supported networks (plug in post-deploy): AdSense, Carbon, EthicalAds.
 * Default: a quiet placeholder so you can see where the slot will sit.
 */

// Flip to true once AdSense / Carbon ID is live in production env.
const ADS_ENABLED = String(process.env.REACT_APP_ADS_ENABLED || "").toLowerCase() === "true";
const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || ""; // e.g. "ca-pub-XXXXXXXXXXXXXXXX"
const ADSENSE_SLOT_POST_SESSION = process.env.REACT_APP_ADSENSE_SLOT_POST_SESSION || "";

export default function AdSlot({ placement = "post-session", className = "" }) {
  const { user } = useAuth();
  const insRef = useRef(null);

  const isFree = !user || user.plan === "free";
  const isAdmin = !!user?.is_admin;
  // Never show to paid users, admins, or during an active study card.
  const show = isFree && !isAdmin;

  useEffect(() => {
    if (!show || !ADS_ENABLED || !ADSENSE_CLIENT) return;
    try {
      // AdSense: push so the script fills the <ins> slot
      // eslint-disable-next-line
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* non-fatal */
    }
  }, [show]);

  if (!show) return null;

  // LIVE AdSense block (only rendered once keys are in env + approved)
  if (ADS_ENABLED && ADSENSE_CLIENT && ADSENSE_SLOT_POST_SESSION) {
    return (
      <div className={`my-8 ${className}`} data-testid={`ad-slot-${placement}`}>
        <div className="eyebrow text-center mb-2 text-[color:var(--ink-faint)]">Sponsored</div>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={ADSENSE_SLOT_POST_SESSION}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  // PLACEHOLDER — shows design/UX of the slot without any network call.
  return (
    <div
      className={`my-8 border rule bg-[color:var(--surface-alt)] p-4 text-center ${className}`}
      data-testid={`ad-slot-placeholder-${placement}`}
    >
      <div className="eyebrow mb-1 text-[color:var(--ink-faint)]">Sponsored · placeholder</div>
      <p className="text-xs text-[color:var(--ink-muted)]">
        A small, respectful ad will appear here after your session ends. Never during study. Upgrade to <strong>Plus</strong> to hide ads entirely.
      </p>
    </div>
  );
}
